import {Component} from "@angular/core";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {ProjectService} from "../../project/project.service";
import {CountryModel} from "../../types/country";
import {ProjectModel, projectGqlModelFac, projectModelFactory} from "../../project/project.model";
import {DatePickerOptions} from "../../datepicker/datepicker.component";
import * as moment from "moment";
import * as uuid from "uuid";
import {WorkflowStep} from "../workflow-step";
import {WorkflowService} from "../workflow.service";
import {WorkflowStepStatus} from "../models/workflow-step-status.enum";
import {ListEditorService} from "../../list-editor/list-editor.service";
import {ListStructure} from "../../list-editor/list-structure";
import {
  dashboardIssuesData,
  expenseData,
  leaseInstructionsData,
  maiInstructionsData,
  milestoneData,
  qualityChecklistData,
} from "./project-details-data";
import {cloneDeep, isEqual} from "lodash";
import {exposeComponentInDebugMode} from "../../misc/expose-component-in-debug-mode.decorator";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {afterRun} from "../../misc/after-run.decorator";
import {ProjectOffHoldDialogComponent} from "./project-off-hold-dialog.component";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import {SmartsheetService} from "../../smartsheet/smartsheet.service";
import {FindCustomerITSMDialogComponent} from "./project-details-find-customer-dialog.component";
import {AppSettingsService} from "../../app-settings/app-settings.service";
import {Subscription} from "rxjs/Subscription";
import {CustomerService} from "../../customer/customer-service";
import {toggleLoading} from "../../misc/loading.decorator";
import {TARGET_SYSTEM} from "../../helpers/app-constants";
import {ProjectCustomerErrorMessagesDialogComponent} from "../../project/project-customer-error-messages-dialog.component";
import {FindCustomerOpportunityGroupITSMDialogComponent} from "./project-details-find-customer-opportunity-group.component";
import {GlobalDealSettingsModel, globalDealSettingsModelFactory} from "../../global-deal-settings/global-deal-settings.model";
import {CustomerOpportunityGroupListResponse, GlobalDealSettingsService, FleetModel,fleetModelFactory} from "../../global-deal-settings/global-deal-settings.service";
import "./spinner.scss";
import { masterAgreementStatusValues } from "../../project/master-agreement-status";
import { CustomerOpportunityGroupModel } from "../../global-deal-settings/customer-opportunity-group.model";
import {HasuraGraphqlService} from "../../graphql/hasura-graphql-service";
import {Query} from "../../graphql/query-constant";
import { PrivilegeViewService } from "../../privilege-view/privilege-view.service";
import { FleetSelectDialogComponent, SelectDialogFleetModel } from "../fleet/fleet-select-dialog.component";

const Moment: any = (moment as any).default || moment;

@Component({
  selector: "project-list",
  templateUrl: "./project-details-pmpsc.template.pug",
  providers: [
    ProjectService,
    CustomerService,
    GlobalDealSettingsService,
    HasuraGraphqlService,
  ],
})
@exposeComponentInDebugMode()
export class ProjectDetailsPmpscComponent extends WorkflowStep {
  public projectId: any;
  public project: ProjectModel = projectModelFactory();
  public globalDealSettings: GlobalDealSettingsModel = globalDealSettingsModelFactory();
  public originalProject: ProjectModel = projectModelFactory(); // Just for Dirty Checks
  public completionDateOptions: DatePickerOptions;
  public error: Error;
  public partnerPurchaseOrders: string;
  public onHoldMessage: string = null;
  public targetSystem: string;
  public lockFleetId: boolean;
  public isMigrationsProject: boolean;
  public customerSet: boolean = false;
  public cogCreationState: number;
  public cogCreationError: any;
  public resellerSet: boolean = false;
  public toggleAdditional: boolean = false;
  public countries: CountryModel[] = [];
  private subscription: Subscription;
  private customerResellerSubscription: Subscription;
  public customerState: number;
  public customerMessage: string;
  public customerErrorMessages: any[];
  public resellerErrorMessages: any[];
  public resellerState: number;
  public resellerMessage: string;
  public doneReseller: boolean = false;
  public doneCustomer: boolean = false;
  public doneCustomerOpportunityGroup: boolean = false;
  public enableCostCenterFileDropdown: boolean = false;
  public canUpdateEndCustomerData: boolean = false;
  public enableMS4CustomerRefresh: boolean = false;
  public spinRefreshMS4Button: boolean = false;
  public loadingCOGName: boolean = false;
  public enableCostCenters: boolean;
  public customerChanged: boolean = false;
  public enabledIndirectDataModel: boolean;
  public createOpportunityInItsm = false;
  public createGlobalOpportunityInItsm = false;
  public existingCogName: string;
  public existingFleetData: FleetModel[];
  public customerReseller = {
    resellerName: "",
    resellerBpId: "",
    itsmResellerId: "",
    customerName: "",
    customerBpId: "",
    itsmCustomerId: "",
  };
  public isFedLlcDomainFlag = false;
  public newFleetName:boolean = false;
  public showSelectFleetButton: boolean;

  public enableApolloProjectQuery: boolean;
  public enableCustomFields: boolean;
  public enableTaxNumberSearch:  boolean;
  public enableGlobalDeals: boolean;
  public customFields = ["customLabel1", "customLabel2", "customLabel3", "customLabel4", "customLabel5", "customLabel6", "customLabel7", "customLabel8", "customLabel9", "customLabel10"];
  // List initialization methods
  public dashboardIssueData = new ListStructure();
  public expenseData = new ListStructure();
  public milestoneData = new ListStructure();
  public qualityChecklistData = new ListStructure();
  public maiInstructionsData = new ListStructure();
  public leaseInstructionsData = new ListStructure();

  private updateDashboardIssueData() {
    this.dashboardIssueData.initialize(
      dashboardIssuesData.generalData,
      dashboardIssuesData.metadata,
      this.listEditorService, // Must be injected
      this.project.dashboardIssues,
    );
  }

  private updateExpenseData() {
    // Set up the currency so that it's marked
    const metadata = cloneDeep(expenseData.metadata);
    metadata.filter(x => x.field === "price").forEach((it) => { it.name = `Price (${this.project.currency})`; });

    this.expenseData.initialize(
      expenseData.generalData,
      metadata,
      this.listEditorService, // Must be injected
      this.project.expenses,
    );
  }

  private updateMilestoneData() {
    this.milestoneData.initialize(
      milestoneData.generalData,
      milestoneData.metadata,
      this.listEditorService, // Must be injected
      this.project.dashboardMilestones,
    );
  }

  private updateQualityChecklistData() {
    this.qualityChecklistData.initialize(
      qualityChecklistData.generalData,
      qualityChecklistData.metadata,
      this.listEditorService, // Must be injected
      this.project.qualityChecklist,
    );
  }

  private updateMaiInstructionsData() {
    this.maiInstructionsData.initialize(
      maiInstructionsData.generalData,
      maiInstructionsData.metadata,
      this.listEditorService, // Must be injected
      this.project.installationInstructions.mai,
    );
  }

  private updateLeaseInstructionsData() {
    this.leaseInstructionsData.initialize(
      leaseInstructionsData.generalData,
      leaseInstructionsData.metadata,
      this.listEditorService, // Must be injected
      this.project.installationInstructions.lease,
    );
  }

  private updateAllLists() {
    this.updateDashboardIssueData();
    this.updateExpenseData();
    this.updateMilestoneData();
    this.updateQualityChecklistData();
    this.updateMaiInstructionsData();
    this.updateLeaseInstructionsData();
  }

  private listsAreDirty() {
    return (
      this.dashboardIssueData.dirty ||
      this.expenseData.dirty ||
      this.milestoneData.dirty ||
      this.qualityChecklistData.dirty ||
      this.maiInstructionsData.dirty ||
      this.leaseInstructionsData.dirty
    );
  }

  private exportAllLists() {
    if (this.dashboardIssueData.dirty) this.project.dashboardIssues = this.dashboardIssueData.export();
    if (this.expenseData.dirty) this.project.expenses = this.expenseData.export();
    if (this.milestoneData.dirty) this.project.dashboardMilestones = this.milestoneData.export();
    if (this.qualityChecklistData.dirty) this.project.qualityChecklist = this.qualityChecklistData.export();
    if (this.maiInstructionsData.dirty) this.project.installationInstructions.mai = this.maiInstructionsData.export();
    if (this.leaseInstructionsData.dirty) {
      this.project.installationInstructions.lease = this.leaseInstructionsData.export();
    }
  }

  /**
   * Converts the Partner Purchase orders from the DB (Array) into a string suitable for display and loads it on ui
   * @param partnerPurchaseOrders {array<string>} Partner Purchase orders from DB
   * @return {void}
   */
  private loadPartnerPurchaseOrders(partnerPurchaseOrders) {
    const localPartnerPurchaseOrders = partnerPurchaseOrders || [];
    this.partnerPurchaseOrders = localPartnerPurchaseOrders.map(po => po.name).join("\n");
  }

  private buildPartnerPurchaseOrders() {
    const poStrings = this.partnerPurchaseOrders.split(/[\r\n]+/) || [];
    return poStrings.map(po => ({name: po}));
  }

  public navigate(queryParams: Params): Promise<boolean> {
    return this.router.navigate(["project-edit"], {queryParams});
  }

  private async getProject(): Promise<ProjectModel> {
    this.enableApolloProjectQuery = await this.appSettingsService.getFeaturePermission(
      "enableApolloProjectQuery",
      this.projectId,
    );

    if (this.enableApolloProjectQuery) {
      Query.PROJECT.variables.projectId = this.projectId;
      const queryParam = {
        projectId: this.projectId,
        query: Query.PROJECT,
        uiAction: "PROJECT",
      };
      const projHasuraResp = await this.hasuraGqlService.getResponseFromHasura(queryParam);
      const finalResponse = projectGqlModelFac(projHasuraResp);
      return finalResponse;
    } else {
      return this.projectService.getProjectById(this.projectId).toPromise();
    }

    /* if (this.enableApolloProjectQuery) {
      let params: any = `
    id
    countryId
    countryName
    startDate
    fleetId
    fleetName
    customerName
    holdDate
    status
    itsmCustomerId
    resellerCustomerId
    name
    partnerPurchaseOrders
    dashboardIssues
    expenses
    dashboardMilestones
    qualityChecklist
    installationInstructions
    customerBpId
    resellerName
    resellerBpId
    opportunityId
    uniqueId
    sourceDocumentationLink
    customerKickoffDate
    estimatedCompletionDate
    estimatedProjectClosureDate
    actualProjectClosureDate
    estimatedTransitionDate
    actualTransitionDate
    enableBulkOrdering
    totalCustomerValue
    requireMaiPagecounts
    currency
    customerPin
    sourceName
    deliveryInformation
    workspaces
    firstInstallDate
    firstOrderDate
    supplyRoutingId
    isFedLLC
    `;
      if (this.enableCustomFields) {
        params += `customLabel1
      customLabel2
      customLabel3
      customLabel4
      customLabel5
      customLabel6
      customLabel7
      customLabel8
      customLabel9
      customLabel10
      currentMaStatus
      customTransactionStatus
      isMpc
      `;
      }
      return this.projectService.getProjectDetails(this.projectId, params);
    } */
  }


  @handleErrors()
  @toggleLoading("loadingCOGName")
  private async checkIfOpportunityAlreadyExistsInITSM() {
    const listCogs: CustomerOpportunityGroupListResponse =
      await this.globalDealSettingsService.listCustomerOpportunityGroups(
        {globalOpportunityId: this.project.opportunityId},
        this.projectId,
        this.targetSystem
      );
      if (listCogs.rows.length == 0) {
        this.createOpportunityInItsm = true;
      } else {
        const {customerOpportunityGroupName = "", customerOpportunityGroupId = ""}: CustomerOpportunityGroupModel = listCogs.rows[0];
        this.globalDealSettings.customerOpportunityGroupName = customerOpportunityGroupName;
        this.globalDealSettings.customerOpportunityGroupId = customerOpportunityGroupId;
        this.globalDealSettings = await this.globalDealSettingsService.saveGlobalDealSettings({
          projectId: this.projectId,
          customerOpportunityGroupName,
          customerOpportunityGroupId
        });

        this.doneCustomerOpportunityGroup = true;
        this.enableCostCenterFileDropdown = true;

        this.existingCogName = customerOpportunityGroupName;
      }
  }

  @handleErrors()
  @toggleLoading("loadingCOGName")
  private async setCogIfExistingInITSM() {
    const listCogs: CustomerOpportunityGroupListResponse =
      await this.globalDealSettingsService.listCustomerOpportunityGroups(
        {search: this.globalDealSettings.customerOpportunityGroupName},
        this.projectId,this.targetSystem,
      );
    if (listCogs.rows.length > 0) {
      const {customerOpportunityGroupName = "", customerOpportunityGroupId = ""}: CustomerOpportunityGroupModel = listCogs.rows[0];
      this.globalDealSettings.customerOpportunityGroupId = customerOpportunityGroupId;
      this.globalDealSettings = await this.globalDealSettingsService.saveGlobalDealSettings({
        projectId: this.projectId,
        customerOpportunityGroupName,
        customerOpportunityGroupId
      });
      this.doneCustomerOpportunityGroup = true;
      this.enableCostCenterFileDropdown = true;
    }
  }

  private async subscribeCustomerReseller() {
    try {
      const observable = this.customerService.getCustomerResellerStatus(this.projectId);

      if (this.customerResellerSubscription && !this.customerResellerSubscription.closed) {
        this.customerResellerSubscription.unsubscribe();
      }
      await new Promise(() => {
        this.customerResellerSubscription = observable.subscribe((result: any) => {
          if (this.project) {
            this.project.customerBpId = result.customerBpId;
            this.project.customerName = result.customerName;
            this.project.resellerName = result.resellerName;
            this.project.resellerBpId = result.resellerBpId;
          }
          this.customerState = result.customerState;
          this.customerMessage = result.customerMessage;
          this.customerErrorMessages = result.customerErrorMessages;
          this.resellerState = result.resellerState;
          this.resellerMessage = result.resellerMessage;
          this.resellerErrorMessages = result.resellerErrorMessages;
          if (result.customerState === 2) {
            this.customerSet = true;
            if (!this.enableGlobalDeals) {
              this.enableCostCenterFileDropdown = true;
            } else {
                this.createCOGByName();
            }
              // Add fleetName default logic here
          if (!this.project.fleetName && this.doneCustomer) {
            this.project.fleetName = this.project.customerName;
            this.updateFleetId()
          }
          }
          if (result.resellerState === 2) this.resellerSet = true;
          if(result.customerState === 1  || result.customerState === 4 || result.customerState === 5 ||
            result.resellerState === 1 || result.resellerState === 4 || result.resellerState === 5){
              this.customerSet = true;
              this.resellerSet = true;
          }
        });
      }).catch((error) => {
        const errorMessage = (error.length === 1) ?
          (error[0].message || "Unknown Error Message") :
          "Customer / Reseller Error(s)";
        throw new Error(errorMessage);
      });
    } catch (e) {
      throw new Error("An unexpected error occurred trying to get the reseller or customer status");
    }
  }

  private async checkCustomerOrResellerBpIdExistence(bpIdToSearch, isReseller = false) {
    const customersListRaw =
      await this.customerService.listMs4Customers(bpIdToSearch, this.projectId, isReseller, 'bpid');
    return customersListRaw.rows;
  }

  @afterRun((self) => { self.loading = false; })
  @handleErrors()
  async loadData(): Promise<void> {
    this.loading = true;
    const featurePermissions = await this.appSettingsService.getFeaturePermissionsList(["enableMS4CustomerRefresh", "enableCustomFields", "enableGlobalDeals", "enableTaxNumberSearch","enableIndirectDataModel","enableCostCenters","showSelectFleetButton"]);
    [this.enableMS4CustomerRefresh,this.enableCustomFields, this.enableGlobalDeals, this.enableTaxNumberSearch,this.enabledIndirectDataModel,this.enableCostCenters,this.showSelectFleetButton] = [
      featurePermissions["enableMS4CustomerRefresh"],
      featurePermissions["enableCustomFields"],
      featurePermissions["enableGlobalDeals"],
      featurePermissions["enableTaxNumberSearch"],
      featurePermissions["enableIndirectDataModel"],
      featurePermissions["enableCostCenters"],
      featurePermissions["showSelectFleetButton"]];
    const arrayPromises: Promise<any>[] = [
      this.getProject(),
    ];

    if (this.enableGlobalDeals) {
      arrayPromises.push(this.globalDealSettingsService.getGlobalDealSettings(this.projectId));
    }
    const [project, globalDealSettings = {}] = await Promise.all(arrayPromises);

    this.globalDealSettings = globalDealSettings;
    // Load Partner Purchase Orders
    this.loadPartnerPurchaseOrders(project.partnerPurchaseOrders);
    if (!project.countryId) project.countryId = "";
    if (!project.startDate) project.startDate = moment().format("YYYY-MM-DD");
    // Fleets
    if (this.enabledIndirectDataModel) {
      // Fleet ID default
      if (!this.isMigrationsProject && !project.fleetId) {
        project.fleetId = uuid.v1().replace(/-/g, ""); // autogenerated
      }
      // Fleet Name default
      if (
        !project.fleetName &&
        (this.targetSystem === TARGET_SYSTEM.PMPS_COMPLETE || this.targetSystem === TARGET_SYSTEM.PMPS_PAGE ||
          this.targetSystem === TARGET_SYSTEM.MP_FLEX_RESELL || this.targetSystem === TARGET_SYSTEM.PMPS_SELL_WITH ||
          this.targetSystem === TARGET_SYSTEM.PMPS_BILL_THROUGH)
      ) {
        project.fleetName = (project.resellerName || project.customerName) ?
          `${project.resellerName}-${project.customerName}` : "";
      }
    }
    if(project.fleetId && project.fleetName && this.showSelectFleetButton){
      this.newFleetName = true
     }
    if(project.isMpc){
       this.newFleetName = true
     }
    this.project = project;
    this.doneCustomer = !!project.customerName;
    this.doneReseller = !!project.resellerName;
    this.doneCustomerOpportunityGroup =
      !!globalDealSettings.customerOpportunityGroupName && !!globalDealSettings.customerOpportunityGroupId;
    this.originalProject = cloneDeep(this.project);
    this.customerSet = !!this.project.itsmCustomerId;
    this.subscribeCustomerReseller(); // we don't await this because it'll never finish

    if (this.enableGlobalDeals && !this.globalDealSettings.customerOpportunityGroupId &&
      this.globalDealSettings.customerOpportunityGroupName) {
      this.setCogIfExistingInITSM();
    }

    this.checkIfOpportunityAlreadyExistsInITSM();

    if (this.globalDealSettings.customerOpportunityGroupId) {
      this.existingFleetData = await this.globalDealSettingsService.getFleetByCustomerOpportunityId(this.projectId, this.globalDealSettings.customerOpportunityGroupId);
    }

    if (this.enableGlobalDeals) {
      if (this.doneCustomerOpportunityGroup) {
        this.enableCostCenterFileDropdown = true;
      }
    } else {
      this.enableCostCenterFileDropdown = this.customerSet;
    }

    this.canUpdateEndCustomerData = this.doneCustomer;

    if (this.project.holdDate) {
      const MS_PER_DAY = 24 * 60 * 60 * 1000;
      const now = new Date();
      const holdDate = new Date(this.project.holdDate);
      const timeOnHold = now.getTime() - holdDate.getTime();
      this.onHoldMessage = (timeOnHold < MS_PER_DAY) ?
        `Project has been on hold ${((timeOnHold * 10 / MS_PER_DAY) / 10).toFixed(1)} day(s)` :
        `Project has been on hold ${Math.ceil(timeOnHold / MS_PER_DAY)} day(s)`;
    }

    if (!project.status) {
      project.status = "open";
    }

    // this.resellerSet = !!(this.project.resellerName ? this.project.resellerName.trim() : this.project.resellerName);

    if (this.project.resellerName && this.project.resellerBpId) {
      this.customerReseller.resellerName = this.project.resellerName;
      this.customerReseller.resellerBpId = this.project.resellerBpId;
    }
    if (this.project.customerName && this.project.customerBpId) {
      this.customerReseller.customerName = this.project.customerName;
      this.customerReseller.customerBpId = this.project.customerBpId;
      this.customerReseller.itsmCustomerId = this.project.itsmCustomerId;
    }
    // Preloading Customer and Reseller
    // When user comes back to project details after customer is set, we are skipping ms4 and broker calls
    if (!this.customerSet || !this.resellerSet) {
      if (
        this.enabledIndirectDataModel && this.project.isMpc &&
        this.project.resellerBpId && this.project.customerBpId
      ) {
        const [resellerAccount, customerAccount] = await Promise.all([
          this.checkCustomerOrResellerBpIdExistence(this.project.resellerBpId, true),
          this.checkCustomerOrResellerBpIdExistence(this.project.customerBpId, false),
        ]);
        if (resellerAccount.length > 0 && customerAccount.length > 0) {
          this.customerReseller.resellerName = resellerAccount[0].customerName;
          this.customerReseller.itsmResellerId = resellerAccount[0].customerId

          this.customerReseller.customerName = customerAccount[0].customerName;
          this.customerReseller.itsmCustomerId = customerAccount[0].customerId;

          await this.projectService.setMs4ItsmAddress(this.projectId, false, this.customerReseller);
        }
      }
    }

    this.updateAllLists();

    if(this.enableCustomFields){
      await this.getItsmCustomLabels();
    }
    this.isFedLlcDomainFlag = this.project.isFedLLC;
  }

  public customerSearchBy: string = 'name';
  checkCustomerSearchBy(type: string) {
    this.customerSearchBy = type;
    this.project.customerName = '';
  }

  public resellerSearchBy: string = 'name';
  checkResellerSearchBy(type: string) {
    this.resellerSearchBy = type;
    this.project.resellerName = '';
  }

  async createCOGByName() {
    if (
      !this.globalDealSettings.customerOpportunityGroupId &&
      this.globalDealSettings.customerOpportunityGroupName
    ) {
      try {
        this.cogCreationState = 1;
        this.globalDealSettings = await this.globalDealSettingsService.createItsmCustomerOpportunityGroup(
          this.globalDealSettings.customerOpportunityGroupName,
          this.projectId,
        );
        this.doneCustomerOpportunityGroup = true;
        this.enableCostCenterFileDropdown = true;
        this.cogCreationState = 2;
      } catch(error) {
        this.cogCreationState = 3;
        this.cogCreationError = error;
      }
    }
  }

  ready() {
    this.error = undefined;
    this.projectId = this.workflowService.workflowConfig.targetId;
    this.targetSystem = this.workflowService.workflowConfig.targetSystem;
    this.isMigrationsProject =
      this.targetSystem === TARGET_SYSTEM.MIGRATION_IPSO_DIRECT_MPS ||
      this.targetSystem === TARGET_SYSTEM.MIGRATION_ORION ||
      this.targetSystem === TARGET_SYSTEM.MIGRATION_IPSO_COMPLETE ||
      this.targetSystem === TARGET_SYSTEM.MIGRATION_IPSO_PAGE;
    this.lockFleetId = !this.isMigrationsProject;
    this.loadData();
  }

  constructor(
    private projectService: ProjectService,
    private customerService: CustomerService,
    public route: ActivatedRoute,
    public router: Router,
    public workflowService: WorkflowService,
    private listEditorService: ListEditorService,
    private dialogService: DialogService,
    private smartsheetService: SmartsheetService,
    private appSettingsService: AppSettingsService,
    private globalDealSettingsService: GlobalDealSettingsService,
    private hasuraGqlService: HasuraGraphqlService,
    private privilegeviewService: PrivilegeViewService,
  ) {
    super(true);
  }

  @toggleLoading()
  @handleErrors()
  async autoSave(): Promise<boolean> {
    this.error = undefined;
    this.exportAllLists();
    this.project.partnerPurchaseOrders = this.buildPartnerPurchaseOrders();
    if ((!this.project.resellerName && !this.doneReseller) || (this.project.resellerName && !this.doneReseller)) {
      throw new Error("Reseller Name is required");
    }
    if(this.resellerState !=2){
      throw new Error("Reseller Onboard is required");
    }
    if ((!this.project.customerName && !this.doneCustomer) || (this.project.customerName && !this.doneCustomer)) {
      throw new Error("Customers Name is required");
    }

    if (
      this.enableGlobalDeals &&
      ((!this.globalDealSettings.customerOpportunityGroupName && !this.doneCustomerOpportunityGroup) ||
      (this.globalDealSettings.customerOpportunityGroupName && !this.doneCustomerOpportunityGroup))
    ) {
      throw new Error("Customer Opportunity Group is required");
    }
    this.project['toCreateFleetName'] = false
    
    if (this.originalProject.status === "hold" && this.project.status !== "hold") {
      const incrementResult = await this.dialogService.showDialog(
        ProjectOffHoldDialogComponent,
        {message: this.onHoldMessage, projectName: this.project.name},
        {backdropColor: "rgba(0, 0, 0, 0.5)"},
      );

      if (incrementResult) {
        if (incrementResult.increment > 0) {
          await this.projectService.saveProject({...this.project, estimatesIncrement: incrementResult});
          await this.workflowService.setStepStatus(WorkflowStepStatus.Completed);
          return true;
        }
        // Else fall bask to the normal save outside this if block
      } else {
        // Return back to the parent screen
        return false;
      }
    }

    if (!this.createOpportunityInItsm && (this.globalDealSettings.customerOpportunityGroupName !== this.existingCogName)) {
      this.createOpportunityInItsm = true;
    }

    await this.projectService.saveProject(this.project, {
      createGlobalOpportunityInItsm: this.createGlobalOpportunityInItsm,
      createOpportunityInItsm: this.createOpportunityInItsm
    });


    await this.workflowService.setStepStatus(WorkflowStepStatus.Completed);
    if (this.workflowService.readableTargetName !== this.project.name) {
      await this.workflowService.updateWorkflowConfig(this.workflowService.workflowConfig.id);
    }

    return true;
  }

  cancel(): void {
    this.router.navigate(["projects"]);
  }

  startDateChange($event): void {
    if ($event.type === "dateChanged") {
      const minDate = moment(this.project.startDate).add(1, "day").toDate();
      const maxDate = moment(new Date("9999-12-31 11:59:59")).toDate(); // max date allowed
      this.completionDateOptions = new DatePickerOptions({minDate, maxDate});
    }
  }

  private checkPartnerPurchaseOrdersAreDirty() {
    const newPO = this.buildPartnerPurchaseOrders();
    const oldPO = this.originalProject.partnerPurchaseOrders;
    return !isEqual(newPO, oldPO);
  }

  checkDirty(): boolean {
    return !isEqual(this.project, this.originalProject) ||
      this.listsAreDirty() ||
      this.checkPartnerPurchaseOrdersAreDirty();
  }

  @handleErrors()
  async promptUserForSmartsheetWorkspaceId() {
    const result = await this.smartsheetService.promptUserForSmartsheetWorkspaceId(
      this.projectId,
      this.project.workspaces,
    );
    if (result && result.smartsheetWorkspaces) {
      this.project.workspaces = result.smartsheetWorkspaces || [];
    }
  }

  async openFleetDialog() {
    const fleets: SelectDialogFleetModel[] = [];

    let fleetModel: FleetModel[] = [];
    let projectFleetId: string = "";
    let projectFleetName: string = "";
    fleetModel = await this.globalDealSettingsService.getFleetByCustomerOpportunityId(this.projectId, this.globalDealSettings.customerOpportunityGroupId);
    if (fleetModel && fleetModel.length > 0) {
      this.existingFleetData = fleetModel;
    }

    if(this.existingFleetData){
      fleets.push(...this.existingFleetData.map(fleet => ({
        ...fleetModelFactory(fleet),
        selected : fleet.fleetId === this.project.fleetId,
        disabled: (!!this.project.isMpc) || (fleet.fleetId === this.project.fleetId),
        uniqueId: uuid.v1(),
      })));
    }

    const isFleetInArray = (fleetId: string, fleetName: string) => {
      return fleets.some(fleet => fleet.fleetId === fleetId && fleet.fleetName === fleetName);
    };


    if (this.project.fleetId && this.project.fleetName && !isFleetInArray(this.project.fleetId, this.project.fleetName)) {
      projectFleetId = this.project.fleetId;
      projectFleetName = this.project.fleetName;
    }

    FleetSelectDialogComponent.showSelectFleetDialogModal(
      this.dialogService,
      { fleet: fleets, disableAddNewFleetButton: !!this.project.isMpc,projectFleetId : projectFleetId, projectFleetName : projectFleetName },
    ).subscribe(async result => {
      if (result && !this.project.isMpc) {
        if(result.newFleets && result.newFleets.length > 0){
          this.newFleetName = true;
        }
        if (result.selectedFleet) {
          this.project.fleetId = result.selectedFleet.fleetId;
          this.project.fleetName = result.selectedFleet.fleetName;
          this.project['toCreateFleetName'] = true;
          await this.projectService.saveProject(this.project, {
          createGlobalOpportunityInItsm: this.createGlobalOpportunityInItsm,
          createOpportunityInItsm: this.createOpportunityInItsm
          });
        } else if (result.newFleet) {
          this.project.fleetId = result.newFleet.fleetId;
          this.project.fleetName = result.newFleet.fleetName;
        }
      }
    });
  }

  onStatusChange() {
    this.project.actualProjectClosureDate = "";
    if (this.project.status === "closed") {
      this.project.actualProjectClosureDate = Moment().format("YYYY-MM-DD");
    }
    this.project.actualDeploymentCompletionDate = "";
    if (this.project.status === "steady") {
      this.project.actualDeploymentCompletionDate = Moment().format("YYYY-MM-DD");
    }
  }

  @toggleLoading()
  @handleErrors()
  async searchCustomerName(isReseller = false) {
    const dataToSearch = isReseller ? this.project.resellerName : this.project.customerName;
    const isCustomerSearchChecked = isReseller ?  this.resellerSearchBy : this.customerSearchBy;
    const result = await FindCustomerITSMDialogComponent.showFindCustomerITSMDialogModal(
      this.dialogService,
      {customerName: dataToSearch, projectId: this.projectId, isReseller, targetSystem: this.targetSystem, customerSearchBy: isCustomerSearchChecked},
    );
    if(isReseller == false && result.customerSearchBy){
      this.customerSearchBy = result.customerSearchBy;
    }
    if(isReseller == true && result.customerSearchBy){
      this.resellerSearchBy = result.customerSearchBy;
    }

    if(isReseller == false && result.customerName){
      this.project.customerName = result.customerName;
    }
    if(isReseller == true && result.customerName){
      this.project.resellerName = result.customerName;
    }

    if (result && result.selectedCustomerInfo) {
      this.loading = true;

      this.customerChanged = !!(
        this.project.itsmCustomerId &&
        result.selectedCustomerInfo.customerId &&
        result.selectedCustomerInfo.customerId !== this.project.itsmCustomerId &&
        !isReseller
      );
      const field = isReseller ? "reseller" : "customer";
      this.project[`${field}Name`] = result.selectedCustomerInfo.customerName;
      this.project[`${field}BpId`] = result.selectedCustomerInfo.bpid;
      this.project[`itsm${field[0].toUpperCase() + field.slice(1)}Id`] = result.selectedCustomerInfo.customerId;
      this.customerReseller[`${field}Name`] = result.selectedCustomerInfo.customerName;
      this.customerReseller[`${field}BpId`] = result.selectedCustomerInfo.bpid;
      this.customerReseller[`itsm${field[0].toUpperCase() + field.slice(1)}Id`] = result.selectedCustomerInfo.customerId;
      this.customerState = 0;
      this.resellerState = 0;

      // update the UI with the results
      if (isReseller) this.doneReseller = true;
      else this.doneCustomer = true;

      if (this.project.resellerName && this.project.customerName && this.project.resellerBpId && this.project.customerBpId) {
        await this.projectService.setMs4ItsmAddress(
          this.projectId,
          isReseller,
          this.enabledIndirectDataModel ? this.customerReseller : result.selectedCustomerInfo,
        );
      }

      this.loading = false;
    }
    else {
      const fields = isReseller ? "reseller" : "customer";
      this.project[`${fields}Name`] = "";
    }
  }

  async updateEndCustomerData() {
    this.spinRefreshMS4Button = true;
    await this.projectService.setMs4ItsmAddress(this.projectId, false, {
      customerBpId: this.project.customerBpId,
      resellerBpId: this.project.resellerBpId,
      itsmResellerId: this.project.resellerCustomerId,
      itsmCustomerId: this.project.itsmCustomerId
    }, true);
    this.spinRefreshMS4Button = false;
  }

  @toggleLoading()
  @handleErrors()
  async searchCustomerOpportunityGroup() {
    this.cogCreationState = 2;
    const result = await FindCustomerOpportunityGroupITSMDialogComponent.showFindOpportunityGroupITSMDialogModal(
      this.dialogService,
      {
        customerOpportunityGroupName: this.globalDealSettings.customerOpportunityGroupName,
        projectId: this.projectId,
        targetSystem: this.targetSystem,
        globalOpportunityId: this.globalDealSettings.globalOpportunityId,
      },
    );

    if (result && result.selectedCustomerOpportunityGroupInfo) {
      const {
        customerOpportunityGroupId,
        customerOpportunityGroupName,
        leadEndCustomerAccountId: leadCustomerItsmId,
      } = result.selectedCustomerOpportunityGroupInfo;

      if (result.customerOpportunityGroupCreated) {
        this.globalDealSettings.customerOpportunityGroupId = customerOpportunityGroupId;
        this.globalDealSettings.customerOpportunityGroupName = customerOpportunityGroupName;
        this.globalDealSettings.leadCustomerItsmId = leadCustomerItsmId;
      } else {
        this.globalDealSettings = await this.globalDealSettingsService.saveGlobalDealSettings({
          projectId: this.projectId,
          customerOpportunityGroupId,
          customerOpportunityGroupName,
          leadCustomerItsmId,
        });

        this.existingFleetData = await this.globalDealSettingsService.getFleetByCustomerOpportunityId(this.projectId, customerOpportunityGroupId);
      }

      this.doneCustomerOpportunityGroup = true;
      this.enableCostCenterFileDropdown = true;
    }
  }

  showErrorMessages(customer = true) {
    const data = customer ? {errorMessages: this.customerErrorMessages} :
      {errorMessages: this.resellerErrorMessages};
    ProjectCustomerErrorMessagesDialogComponent.showDialog(
      this.dialogService,
      data,
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.subscription = this.appSettingsService.countries.subscribe((countries) => {
      this.countries = countries;
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.subscription && !this.subscription.closed) {
      this.subscription.unsubscribe();
    }

    if (this.customerResellerSubscription && !this.customerResellerSubscription.closed) {
      this.customerResellerSubscription.unsubscribe();
    }
  }

  async getItsmCustomLabels() {
    Query.CUSTOM_LABELS.variables.projectId = this.projectId;
    const params = {
      projectId: this.projectId,
      query: Query.CUSTOM_LABELS,
      uiAction: "PROJECT-CUSTOM-LABELS",
    };
    const projectCustomlabels = await this.hasuraGqlService.getResponseFromHasura(params);
    if (projectCustomlabels && projectCustomlabels.body && projectCustomlabels.body.data &&
      projectCustomlabels.body && projectCustomlabels.body.data.project &&
      projectCustomlabels.body && projectCustomlabels.body.data.project.length > 0) {
      const labelValues = projectCustomlabels.body.data.project[0].customHeaders || {};
      const keys = Object.keys(labelValues);
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        this.project[`customLabel${i + 1}`] = labelValues[key];
      }
    }
  }

  updateFleetId() {
    if (!this.project.isMpc && this.existingFleetData && this.existingFleetData.length > 0) {
      const matchingFleet:any = this.existingFleetData.find((fleet:any) => fleet.fleetName.toLowerCase() === this.project.fleetName.toLowerCase());
      if (!matchingFleet) {
        this.project.fleetId = uuid.v1().replace(/-/g, "");
      } else {
        this.project.fleetId = matchingFleet.fleetId;
      }
    }else{
      this.project.fleetId = uuid.v1().replace(/-/g, "");
    }
  }
}
